<template>
  <div class="app-page__container app-page__container--no-sidebar">
    <div v-if="!isLoading">
      <div style="width: auto;">
        <UpgradeSubscriptionModal
          :is-modal-active="isSubscriptionModalActive"
          :workspace-user-count="usersAndInvitationsCount"
          @close="handleSubscriptionModalClose"
        />
      </div>
      <h5 class="title is-5">
        Invite User
      </h5>
      <div class="card">
        <div class="card-content">
          <form @submit.prevent="handleSubmitInviteUser">
            <BMessage v-if="formSubmissionError" type="is-danger">
              {{ formSubmissionError.message }}
            </BMessage>
            <div class="columns u-m-b-0">
              <BField
                class="column u-p-b-0"
                :label="inviteUserForm.newUserEmail.label"
                :type="formErrors[inviteUserForm.newUserEmail.name] ? 'is-danger' : ''"
                :message="formErrors[inviteUserForm.newUserEmail.name] || ''"
              >
                <BInput
                  v-model="inviteUserForm.newUserEmail.value"
                  :disabled="isFormSubmissionInProgress"
                  @input="handleTouchForm"
                />
              </BField>
              <BField
                :label="inviteUserForm.newUserTeam.label" class="column u-p-b-0"
                :type="formErrors[inviteUserForm.newUserTeam.name] ? 'is-danger' : ''"
                :message="formErrors[inviteUserForm.newUserTeam.name] || ''"
              >
                <BSelect
                  :name="inviteUserForm.newUserTeam.name"
                  v-model="inviteUserForm.newUserTeam.value"
                  expanded
                  @input="handleTouchForm"
                >
                  <option
                    v-for="teamOption in shapedTeamOptions"
                    :value="teamOption.id"
                    :key="teamOption.id"
                    :disabled="teamOption.disabled"
                    :selected="teamOption.id === inviteUserForm.newUserTeam.value"
                  >
                    {{ teamOption.teamName || teamOption.id }}
                  </option>
                </BSelect>
              </BField>
            </div>
            <BField class="has-text-right u-p-t">
              <BButton
                type="is-info"
                native-type="submit"
                :loading="isFormSubmissionInProgress"
              >
                Invite User
              </BButton>
            </BField>
          </form>
        </div>
      </div>
    </div>
    <div v-else style="position: relative; min-height: 150px;">
      <BLoading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></BLoading>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as SubscriptionTypes from '@/constants/Subscription.types';
import * as TeamActions from '@/store/actions/Team.actions';
import * as InvitationActions from '@/store/actions/Invitation.actions';
import * as ValidationService from '@/services/Validation.service';
import * as InvitationConstants from '@/constants/Invitation.constants';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import { confirmLeaveWithUnsavedChanges } from '@/helpers/formHelpers';
import UpgradeSubscriptionModal from '@/components/UpgradeSubscriptionModal.vue';

export default {
  name: 'InviteUserView',

  components: {
    UpgradeSubscriptionModal,
  },

  data() {
    return {
      inviteUserForm: {
        newUserEmail: {
          label: 'Email Address',
          name: 'Email Address',
          value: '',
          required: true,
          validator: ValidationService.validateEmail,
        },
        newUserTeam: {
          label: 'Team',
          name: 'Team',
          value: '-- Select a Team --',
          required: true,
          validator: ValidationService.validateTeamSelection,
        },
      },
      formErrors: {},
      formSubmissionError: null,
      isFormTouched: false,
      isFormSubmissionInProgress: false,
      areWorkspaceTeamsLoading: true,
      arePendingInvitationsLoading: true,
      isSubscriptionModalActive: false,
    };
  },

  computed: {
    ...mapState({
      teams: state => state.team.teams,
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
    }),
    ...mapGetters([
      'activeWorkspace',
      'loggedInUser',
      'pendingInvitations',
      'activeWorkspaceSubscription',
    ]),
    usersAndInvitationsCount() {
      return (this.activeWorkspace.userCount || 0) + (Object.keys(this.pendingInvitations).length || 0);
    },
    shapedTeamOptions() {
      if (isObjectEmpty(this.teams)) return [];

      return [
        { disabled: true, id: '-- Select a Team --' },
        ...Object.values(this.teams),
      ];
    },
    inviteUserFormPayload() {
      return {
        inviteeEmail: this.inviteUserForm.newUserEmail.value,
        workspaceId: this.activeWorkspaceId,
        workspaceName: this.activeWorkspace.workspaceName,
        statusId: InvitationConstants.invitationStatusIds.PENDING,
        inviterId: this.loggedInUser.id,
        inviterFirstName: this.loggedInUser.firstName,
        inviterLastName: this.loggedInUser.lastName,
        teamId: this.inviteUserForm.newUserTeam.value,
        teamName: this.teams[this.inviteUserForm.newUserTeam.value].teamName,
      };
    },
    isLoading() {
      return this.areWorkspaceTeamsLoading || this.arePendingInvitationsLoading;
    },
  },

  created() {
    this.fetchInitialData();
  },

  updated() {
    this.handleOpenSubscriptionModal();
  },

  beforeRouteLeave (to, from, next) { // eslint-disable-line
    confirmLeaveWithUnsavedChanges(this.isFormTouched, next);
  },

  methods: {
    ...mapActions({
      fetchWorkspaceTeams: TeamActions.FETCH_WORKSPACE_TEAMS,
      createInvitation: InvitationActions.CREATE_INVITATION,
      fetchPendingWorkspaceInvitations: InvitationActions.FETCH_PENDING_WORKSPACE_INVITATIONS,
    }),
    async fetchInitialData() {
      this.areWorkspaceTeamsLoading = true;
      this.arePendingInvitationsLoading = true;
      await this.fetchWorkspaceTeams({ workspaceId: this.activeWorkspaceId });
      await this.fetchPendingWorkspaceInvitations({ workspaceId: this.activeWorkspace.id });
      this.areWorkspaceTeamsLoading = false;
      this.arePendingInvitationsLoading = false;
    },
    async handleSubmitInviteUser() {
      this.formSubmissionError = null;
      this.formErrors = ValidationService.runFormValidation({ ...this.inviteUserForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isFormSubmissionInProgress = true;
      try {
        await this.createInvitation({ newInvitation: this.inviteUserFormPayload });

        this.isFormSubmissionInProgress = false;
        this.isFormTouched = false;
        this.$router.push('/users');
      } catch (err) {
        this.isFormSubmissionInProgress = false;
        this.formSubmissionError = err;
      }
    },
    handleTouchForm() {
      this.isFormTouched = true;
    },
    handleSubscriptionModalClose({ hasUserUpgraded }) {
      this.isSubscriptionModalActive = false;
      if (!hasUserUpgraded) this.$router.go(-1);
    },
    handleOpenSubscriptionModal() {
      if (
        this.usersAndInvitationsCount > 4
        && this.activeWorkspaceSubscription.stripePricingPlan === SubscriptionTypes.STRIPE_PRICE_TYPE_FREE_FOREVER(this.$env)
        && !this.activeWorkspaceSubscription.isExempt
      ) {
        this.isSubscriptionModalActive = true;
      }
    },
  },
};
</script>

<style lang="sass" scoped>

</style>
